<template>
  <div class="global-session">
    <div class="global-session__body">
      <div v-if="isOtherSessionRunning" class="global-session__session-running">
        <div class="global-session__session-running__body">
          <p class="global-session__subtitle">
            Another lab is running
          </p>
          <p class="global-session__text">
            You can't start this lab until you stop your other running lab.
          </p>
          <p class="global-session__lab-running">
            Lab running: {{ globalRunningLabName }}
          </p>
          <p class="global-session__lab-running">
            {{ formatRunningTime(runningTime) }}
          </p>
          <button
            class="btn btn--tertiary btn--default-icon-color btn--tertiary--with-icon btn btn--tertiary--with-icon--right btn btn--tertiary--small global-session__flat-btn"
            @click="stopGlobalSession()"
          >
            <span class="global-session__flat-btn-text">
              STOP LAB
            </span>
            <StopLabIcon v-if="!isLoadingGlobalSession" />
            <LoadingSmallSpinner v-if="isLoadingGlobalSession" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSmallSpinner from '@/components/loaders/LoadingSmallSpinner.vue'
import StopLabIcon from '@/assets/icons/course/lab/stop-lab-icon.svg'
import CONSTANTS from '@/constants'
import { mapState } from 'vuex'

export default {
  name: 'PTACardGlobalSession',
  components: {
    StopLabIcon: { name: 'StopLabIcon', ...StopLabIcon },
    LoadingSmallSpinner,
  },
  props: {
    currentLab: {
      type: Object,
      required: true,
    },
    currentSession: {
      type: Object,
      required: false,
    },
    labsRequestPayload: {
      type: Object,
      required: false,
    },
  },
  data: () => {
    return {
      isLoadingGlobalSession: false,
      runningTime: null,
      runningTimeIntervalId: null,
    }
  },
  computed: {
    ...mapState({
      runningLabSession: state => state.labs.runningLabSession,
    }),
    status() {
      const sessionStatus =
        this.currentSession && this.currentSession.status
          ? this.currentSession.status
          : CONSTANTS.LABS.SESSION_STATUS.IDDLE
      return sessionStatus
    },
    isInitialStatus() {
      return [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.TERMINATING,
        CONSTANTS.LABS.SESSION_STATUS.TERMINATED,
        'error',
        null,
      ].includes(this.status)
    },
    isRunningStatus() {
      return [
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
      ].includes(this.status)
    },
    isStoppedStatus() {
      return [CONSTANTS.LABS.SESSION_STATUS.STOPPED].includes(this.status)
    },
    globalRunningSession() {
      return this.currentLab.global_running_session
    },
    isOtherSessionRunning() {
      return (
        this.globalRunningSession &&
        this.globalRunningSession.id !== this.currentSession?.id
      )
    },
    globalRunningLabName() {
      return this.globalRunningSession && this.globalRunningSession.lab.name
    },
  },
  watch: {
    isOtherSessionRunning: {
      inmediate: true,
      handler() {
        if (this.isOtherSessionRunning) {
          this.handleRunningTimeInterval()
        }
      },
    },
  },
  mounted() {
    if (this.isOtherSessionRunning) {
      this.handleRunningTimeInterval()
    }
  },
  beforeDestroy() {
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
  },
  methods: {
    formatRunningTime(secs) {
      const sec_num = parseInt(secs, 10)
      const hours = Math.floor(sec_num / 3600)
      const minutes = Math.floor(sec_num / 60) % 60
      const seconds = sec_num % 60
      return [hours, minutes, seconds]
        .map(v => (v < 10 ? `0${v}` : v))
        .join(':')
    },
    increaseRunningTime() {
      this.runningTime += 1
    },
    handleRunningTimeInterval() {
      if (!this.globalRunningSession) return
      this.setInitialRunningTime()
      if (
        this.globalRunningSession.status === 'started' &&
        !this.runningTimeIntervalId
      ) {
        this.runningTimeIntervalId = setInterval(this.increaseRunningTime, 1000)
      } else if (this.globalRunningSession.status !== 'started') {
        this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
      }
    },
    setInitialRunningTime() {
      this.runningTime = this.globalRunningSession.running_time
    },
    async stopGlobalSession() {
      this.isLoadingGlobalSession = true
      const statusToStop =
        this.globalRunningSession?.lab.lab_type === 'pta'
          ? CONSTANTS.LABS.SESSION_STATUS.TERMINATED
          : CONSTANTS.LABS.SESSION_STATUS.STOPPED
      await this.$store.dispatch('labs/partialUpdateLabSession', {
        labId: this.globalRunningSession?.lab?.id,
        parentId: this.globalRunningSession?.metadata?.context?.parent_id,
        parentType: this.globalRunningSession?.metadata?.context?.parent_type,
        sessionId: this.globalRunningSession?.id,
        status: statusToStop,
        cleanGlobalSession: true,
      })
      this.isLoadingGlobalSession = false
    },
  },
}
</script>

<style lang="scss" scoped>
.global-session {
  max-width: 100%;
  width: 100%;
  background: linear-gradient(
    111.02deg,
    rgba(18, 18, 18, 0.7) 3.86%,
    rgba(18, 18, 18, 0) 107.96%
  );
  /* Gray/Gray 4 */

  border: 1px solid var(--tundora-gray);
  box-sizing: border-box;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: flex-start;
  justify-content: center;
  padding: 30px;
  position: relative;
  overflow: hidden;
  @include tablet {
    max-width: 550px;
  }
  &__body {
    text-align: left;
  }
  &__subtitle {
    font-weight: 700;
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    color: var(--white);
    &-icon {
      color: var(--lighter-gray);
      font-size: 26px;
      margin: -3px 5px 0 0;
    }
  }
  &__text {
    margin-bottom: 10px;
    &--highlight {
      color: var(--orange);
    }
  }
  &__session-running {
    display: flex;
    justify-content: flex-start;
    .global-session__flat-btn svg {
      margin-left: 5px;
      fill: var(--light-blue-gray);
      path {
        fill: transparent;
        stroke: var(--light-blue-gray);
      }
    }
  }
  &__flat-btn {
    padding: 5px 9px;
  }
  &__flat-btn-text {
    color: var(--light-blue-gray);
    font-size: rem(14);
    padding-top: 3px;
  }
  &__lab-running {
    color: var(--white);
    margin-bottom: 5px;
  }
  .btn__link {
    margin-right: 20px;
  }
}
</style>
